import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { analyticsAllowed } from "../lib/cookieConsent";
import { Reveal, useSeo } from "../lib/ui";

const TRACK_KEYS = ["professional", "national", "women_in_football"];

const ScoreBar = ({ score, max }) => (
  <div>
    <div className="h-1.5 w-full bg-zinc-900">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${(score / max) * 100}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="h-full bg-maasai"
      />
    </div>
  </div>
);

const PlayerRow = ({ p, max, i }) => (
  <Reveal delay={Math.min(i * 0.03, 0.3)}>
    <Link
      to={`/womens-game/${p.slug}`}
      data-testid={`womens-row-${p.slug}`}
      className="group grid grid-cols-[48px_1fr] items-center gap-5 py-6 md:grid-cols-[64px_1fr_180px_90px]"
    >
      <span className="font-display text-3xl font-bold leading-none text-zinc-700 transition-colors group-hover:text-ochre md:text-4xl">
        {String(p.rank).padStart(2, "0")}
      </span>
      <div>
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-white md:text-xl">
          {p.name}
        </h3>
        <p className="mono mt-1 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">
          {p.position} · {p.club} · {p.country} · {p.era}
        </p>
        <p className="mt-2 max-w-xl text-xs leading-snug text-ochre/90 transition-colors group-hover:text-white">
          {p.title}
        </p>
      </div>
      <div className="col-span-2 md:col-span-1">
        <ScoreBar score={p.total_score} max={max} />
        <div className="mt-2 flex gap-3 text-[0.6rem] uppercase tracking-wider text-zinc-400">
          <span>L {p.scores.league_strength}</span>
          <span>P {p.scores.prestige}</span>
          <span>T {p.scores.titles}</span>
          <span>I {p.scores.impact}</span>
        </div>
      </div>
      <span className="col-span-2 font-display text-2xl font-bold text-ochre md:col-span-1 md:text-right md:text-3xl">
        {p.total_score}
      </span>
    </Link>
  </Reveal>
);

const PersonCard = ({ person, i }) => (
  <Reveal delay={Math.min(i * 0.04, 0.3)}>
    <Link
      to={`/womens-game/${person.slug}`}
      data-testid={`womens-person-${person.slug}`}
      className="group block border border-zinc-800 bg-[#0b0b0b] p-6 transition-colors hover:border-ochre md:p-8"
    >
      <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-maasai">{person.category}</p>
      <h3 className="mt-3 font-display text-lg font-bold uppercase leading-tight text-white group-hover:text-ochre md:text-xl">
        {person.name}
      </h3>
      <p className="mono mt-2 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-ochre">
        {person.role}
      </p>
      <p className="mt-4 line-clamp-4 text-sm leading-relaxed text-zinc-400">{person.body}</p>
      <span className="mono mt-5 inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500 group-hover:text-ochre">
        Read the profile <ArrowUpRight size={11} />
      </span>
    </Link>
  </Reveal>
);

export default function WomensGame() {
  const [d, setD] = useState(null);
  const [track, setTrack] = useState("professional");

  useSeo({
    title: "The Women's Game — KickStartKenyaFootball",
    description:
      "Kenyan women in professional and international football, ranked on the same eighty-point model as the Hall of Fame, plus the coaches, referees and administrators running the game.",
  });

  useEffect(() => {
    api.get("/womens-game").then((r) => setD(r.data));
    if (analyticsAllowed()) api.post("/track", { kind: "page", ref: "/womens-game" }).catch(() => {});
  }, []);

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="womens-loading" />;

  const tracks = d.tracks;
  const active = tracks.find((t) => t.key === track);
  const players = d.players.filter((p) => p.track === track);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="womens-game-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <p className="overline text-ochre">The women&rsquo;s game</p>
        <h1 className="display-1 mt-5">Kenya&rsquo;s Women In Football</h1>
        <p className="body-copy mt-6 max-w-3xl">
          Past, present and coming through: the Kenyan women who turned professional abroad, the Harambee
          Starlets who qualified before anyone was willing to fund them, and the coaches, referees and
          administrators who run the game. The players are scored on the same four criteria as the Hall of
          Fame &mdash; League Strength (25), Prestige (20), Titles Won (20) and Impact (15), for a maximum of
          eighty.
        </p>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="womens-counts">
          {[
            ["Ranked players", d.counts.players],
            ["In the professional game", d.counts.professional],
            ["National game profiles", d.counts.national],
            ["Coaches, referees, officials", d.counts.women_in_football],
          ].map(([label, value]) => (
            <div key={label} className="bg-black p-5">
              <p className="font-display text-2xl font-bold leading-none text-ochre md:text-3xl">{value}</p>
              <p className="mono mt-3 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
                {label}
              </p>
            </div>
          ))}
        </div>

        <div className="no-scrollbar mt-12 flex gap-1 overflow-x-auto" data-testid="womens-tabs">
          {tracks.map((t) => (
            <button
              key={t.key}
              onClick={() => setTrack(t.key)}
              data-testid={`womens-tab-${t.key}`}
              className={`whitespace-nowrap px-5 py-3 text-[0.62rem] font-bold uppercase tracking-[0.18em] transition-colors ${
                track === t.key
                  ? "bg-ochre text-black"
                  : "border border-zinc-800 text-zinc-400 hover:border-ochre hover:text-ochre"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="mono mt-4 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
          {active?.note}
        </p>

        {track === "women_in_football" ? (
          <div className="mt-10 grid gap-px bg-zinc-800 md:grid-cols-2" data-testid="womens-people-list">
            {d.women_in_football.map((person, i) => (
              <PersonCard key={person.slug} person={person} i={i} />
            ))}
          </div>
        ) : (
          <div
            className="mt-10 divide-y divide-zinc-900 border-t border-zinc-900"
            data-testid="womens-player-list"
          >
            {players.map((p, i) => (
              <PlayerRow key={p.slug} p={p} max={d.max_score} i={i} />
            ))}
          </div>
        )}

        <div className="mt-16 flex flex-wrap gap-4">
          <Link
            to="/hall-of-fame"
            className="inline-flex items-center gap-2 bg-ochre px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-black transition-colors hover:bg-white"
            data-testid="womens-to-hof"
          >
            <Trophy size={14} /> The Hall of Fame Top 20
          </Link>
          <Link
            to="/firsts"
            className="inline-flex items-center gap-2 border border-zinc-700 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre"
            data-testid="womens-to-firsts"
          >
            First To Do It <ArrowUpRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  );
}
